import React, { createContext, PropsWithChildren, useContext } from "react";

import { RangeValue } from "@rentablo/components";
import { Investment } from "@rentablo/api/apiv1";
import { SecurityMetricValue } from "./components/SecurityMetrics";
import { useFundamentalsMetrics } from "./hooks/useFundamentalsMetricsMenu";

export interface FundamentalsContextValue {
  menuItems?: { key: string; label: string }[];
  selectedMetrics: SecurityMetricValue[];
  showMetric: (key: string) => void;
  hideMetric: (key: string) => void;
  toggleMetric: (key: string) => void;
  changeMetric: (
    mainMetricKey: string,
    val: { range?: RangeValue; secondMetricKey?: string }
  ) => void;
  isLoading: boolean;
  isInvestmentFund: boolean;
  isInvestmentStock: boolean;
}

const FundamentalsContext = createContext<FundamentalsContextValue | null>(
  null
);

interface FundamentalsProviderProps extends PropsWithChildren<{}> {
  investment?: Investment;
  investmentTypeOverride?: string | null;
}

export const FundamentalsProvider = ({
  investment,
  investmentTypeOverride,
  children,
}: FundamentalsProviderProps) => {
  const {
    menuItems,
    selectedMetrics,
    showMetric,
    hideMetric,
    toggleMetric,
    changeMetric,
    isLoading,
  } = useFundamentalsMetrics();

  const typeFromInvestment = investment?.type ?? null;
  const effectiveType = investmentTypeOverride ?? typeFromInvestment ?? null;

  const isInvestmentFund =
    effectiveType === "21_fund" || effectiveType === "22_etf";
  const isInvestmentStock = effectiveType === "11_stock";

  const value: FundamentalsContextValue = {
    menuItems,
    selectedMetrics,
    showMetric,
    hideMetric,
    toggleMetric,
    changeMetric,
    isLoading,
    isInvestmentFund,
    isInvestmentStock,
  };

  return (
    <FundamentalsContext.Provider value={value}>
      {children}
    </FundamentalsContext.Provider>
  );
};

export const useFundamentalsContext = () => {
  const ctx = useContext(FundamentalsContext);

  if (!ctx) {
    throw new Error(
      "useFundamentalsContext must be used within a FundamentalsProvider"
    );
  }

  return ctx;
};
